import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, MessageCircle, Pencil, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { ConvertModal } from "@/components/ConvertModal";
import { waLink, CLIENT_STATUS_LABELS, ENTITY_STATUS_CLS } from "@/lib/prospectMeta";

function Row({ label, value }) {
  if (!value) return null;
  return <div className="py-1.5 border-b border-slate-100 last:border-0"><p className="text-xs text-slate-400">{label}</p><p className="text-sm text-slate-800">{value}</p></div>;
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [showEdit, setShowEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/clients/${id}`); setC(data); }
    catch { toast.error("Data klien tidak ditemukan."); navigate("/siswa-klien"); }
    finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/prospects/meta").then(({ data }) => setMembers(data.members)).catch(() => {}); }, []);

  if (loading || !c) return <AppLayout><div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div></AppLayout>;

  return (
    <AppLayout>
      <button onClick={() => navigate("/siswa-klien")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="client-back"><ArrowLeft className="h-4 w-4" /> Kembali ke Siswa & Klien</button>
      <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-3xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900" data-testid="client-name">{c.institution_name}</h1>
            <p className="text-sm text-slate-500 font-mono">{c.client_no}</p>
            <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs border ${ENTITY_STATUS_CLS[c.status] || "bg-slate-100"}`} data-testid="client-status">{CLIENT_STATUS_LABELS[c.status] || c.status}</span>
          </div>
          <div className="flex gap-2">
            <a href={waLink(c.phone_normalized)} target="_blank" rel="noreferrer" data-testid="client-wa" className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-2 rounded-lg"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
            <button onClick={() => setShowEdit(true)} data-testid="client-edit" className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-slate-50"><Pencil className="h-4 w-4" /> Edit</button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          <Row label="Jenis Lembaga" value={c.institution_type} />
          <Row label="Kontak/PIC Lembaga" value={c.contact_person} />
          <Row label="Jabatan" value={c.position} />
          <Row label="WhatsApp" value={c.phone_normalized} />
          <Row label="Email" value={c.email} />
          <Row label="Alamat" value={c.address} />
          <Row label="Program" value={c.program} />
          <Row label="Jumlah Peserta" value={c.participant_count} />
          <Row label="Metode" value={c.mode} />
          <Row label="Rencana Pelaksanaan" value={c.implementation_plan} />
          <Row label="Tanggal Bergabung" value={c.join_date} />
          <Row label="PIC IEC" value={c.assigned_to_name} />
          <Row label="Catatan" value={c.notes} />
        </div>
        {c.prospect_id && (
          <button onClick={() => navigate(`/prospek/${c.prospect_id}`)} className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#f97316] hover:underline" data-testid="client-prospect-link"><Link2 className="h-4 w-4" /> Lihat prospek asal</button>
        )}
      </div>
      {showEdit && <ConvertModal mode="edit" type="lembaga" entity={c} members={members} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
    </AppLayout>
  );
}
